import { LitElement, html, css, styleMap, utils } from '@hh/global.js'
const { throttle, } = utils

class HHStickyHeader extends LitElement {
  static properties = {
    closeWhenHidden: {
      type: String,
      attribute: 'close-when-hidden',
    },
    _state: {
      type: Number,
      state: true,
    },
    _height: {
      type: Number,
      state: true,
    },
    _initialHeight: {
      type: Number,
      state: true,
    },
    enableTransparent: {
      type: Boolean,
      attribute: 'enable-transparent',
    },
    transparent: {
      type: Boolean,
      reflect: true,
    }
  }

  constructor() {
    super()

    this.headerBounds = {}
    this.currentScrollTop = 0
    this.closeWhenHidden = 'hh-details-modal'

    this._state = -1

    this.mediaQuery = window.matchMedia('(max-width: 990px)')
    this.isMobileOrTablet = this.mediaQuery.matches
  }

  connectedCallback() {
    super.connectedCallback()
    document.body?.classList?.add('has-sticky-header')
    this.createObserver()

    this._onScroll = throttle(() => this.handleScroll(), 75)
    window.addEventListener('scroll', this._onScroll)

    this._onMediaChange = (e) => {
      this.isMobileOrTablet = e.matches
    }

    this.mediaQuery.addEventListener('change', this._onMediaChange)

    setTimeout(() => {
      this.setSpacerHeight()
    })
  }

  disconnectedCallback() {
    super.disconnectedCallback()
    window.removeEventListener('scroll', this._onScroll)
    document.body?.classList?.remove('has-sticky-header')
    this.mediaQuery.removeEventListener('change', this._onMediaChange)
  }

  firstUpdated() {
    setTimeout(() => {
      this.setSpacerHeight()
    })

    if (this.enableTransparent) {
      this.toggleTransparent()
    }
  }

  updated(changedProperties) {
    if (changedProperties.has('_state')) {
      document.body.classList[this._state !== 1 ? 'add' : 'remove'](
        'sticky-header-showing'
      )
    }
  }

  createObserver() {
    let observer = new IntersectionObserver((entries, observer) => {
      this.headerBounds = entries[0].intersectionRect
      observer.disconnect()
    })

    observer.observe(this)
  }

  setSpacerHeight() {
    const { height: _heightAnnouncementbar } = document
      .querySelector('.js-announcement-bar')
      ?.getBoundingClientRect() || {height: 0}
    const { height: _heightHeader } = document
      .querySelector('.js-header')
      .getBoundingClientRect()

    this._initialHeight = _heightAnnouncementbar + _heightHeader
    this.height = this.enableTransparent ? 0 : this._initialHeight
    this._state = 0
    HH.setCSSVariable('--hh-spacer-height', `${this.height}px`)

    HH.setCSSVariable('--hh-header-height', `${_heightHeader}px`)
    HH.setCSSVariable('--hh-announcement-bar-height', `${_heightAnnouncementbar}px`)
    HH.setCSSVariable('--hh-total-header-height', `${this._initialHeight}px`)
  }

  async toggleTransparent() {
    await this.updateComplete
    if (this.isAnyModalOpen() || this.isMegaMenuOpen()) {
      this.transparent = false
      return
    }

    const scrollTop = window.pageYOffset || document.documentElement.scrollTop
    this.transparent = scrollTop <= this._initialHeight
  }

  isAnyModalOpen() {
    return utils.all('hh-details-modal', document).some((element) => {
      return (
        element.hasAttribute('toggle-transparent-header') &&
        element.hasAttribute('is-open')
      )
    })
  }

  isMegaMenuOpen() {
    const megaMenu = document.querySelector('hh-mega-menu-drawer')
    return megaMenu && megaMenu.open
  }

  hide() {
    if (this._state === 1) {
      return
    }
    this._state = 1

    HH.setCSSVariable('--hh-header-height', '0px')
    HH.setCSSVariable('--hh-announcement-bar-height', '0px')
    HH.setCSSVariable('--hh-total-header-height', '0px')

    this.header?.querySelectorAll(this.closeWhenHidden)?.forEach((element) => {
      element.setAttribute('open', false)
    })
  }

  reveal() {
    if (this._state === 2) {
      return
    }

    this.setSpacerHeight()
    this._state = 2
  }

  reset() {
    if (this._state === 0) {
      return
    }

    this.setSpacerHeight()
    this._state = 0
  }

  onMouseEnter() {
    if (!this.enableTransparent) return

    this.transparent = false
  }

  onMouseLeave() {
    if (!this.enableTransparent || this.isMobileOrTablet) return
    this.toggleTransparent()
  }

  onMobileMenuOpened(e) {
    if (!this.enableTransparent) return

    const element = e.detail.value
    if (element.hasAttribute('toggle-transparent-header')) {
      this.transparent = false
    }
  }

  onMobileMenuClosed(e) {
    if (!this.enableTransparent) return

    const element = e.detail.value
    if (element.hasAttribute('toggle-transparent-header')) {
      this.toggleTransparent()
    }
  }

  externalRevealHeader() {
    if (this._state === 0) return
    this._state = 0
  }

  handleScroll() {
    const scrollTop = window.pageYOffset || document.documentElement.scrollTop

    if(!this.isMobileOrTablet) {
      if (this._state !== 0) {
        this.reset()
      }
    } else {
      if (scrollTop > this.currentScrollTop) {
        this.hide()
      } else {
        this.reveal()
      }
    }

    this.currentScrollTop = scrollTop <= 0 ? 0 : scrollTop

  }

  render() {
    return html`<div
        class="container"
        style="${styleMap({
      position: this._state === -1 ? 'relative' : 'fixed',
      transform: this._state === 1 ? `translateY(-100%)` : 'translateY(0)',
    })}"
        @mouseenter=${this.onMouseEnter}
        @mouseleave=${this.onMouseLeave}
        @modalOpened=${this.onMobileMenuOpened}
        @modalClosed=${this.onMobileMenuClosed}
      >
        <slot></slot>
      </div>
      <div
        class="spacer"
      ></div>`
  }

  static styles = css`
    .container {
      left: 0;
      width: 100%;
      z-index: var(--hh-z-index, 100);
      transform: translateY(0);
      transition: transform var(--hh-duration-long, 500ms);
      background-color: var(--hh-background, transparent);
    }

    .spacer {
      position: relative;
      height: var(--hh-spacer-height);
    }
  `
}

customElements.define('hh-sticky-header', HHStickyHeader)
